import { useCallback, useEffect, useState } from "react";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import {
  listCaptureTokens,
  createCaptureToken,
  revokeCaptureToken,
  type CaptureTokenPublic,
} from "../cma/api";
import { blockStyle } from "../ui/Block";

/**
 * CONNECT THE BROWSER EXTENSION — `/extension` (2026-08-01).
 *
 * WHY THIS EXISTS. The capture-token endpoints have been in `server/projects.py` since the
 * extension shipped, session-authed and working. The only UI for them was in Apex's `index.html`
 * (`genToken` / `loadTokens` / `revokeToken` at :1795-1809). So an agent working in `/next` had no
 * way to get the credential the extension needs — the feature was live and unreachable.
 *
 * That is a gap today and a REGRESSION the moment `valubrief.ai` points at this app: the extension
 * opens the API base's root after a capture, which would then be this app, and there would be
 * nowhere in it to obtain or revoke the token it authenticates with. This screen is a prerequisite
 * for that domain move, not a convenience.
 *
 * THE ONE-SHOT RULE, AND WHY THE SCREEN IS SHAPED AROUND IT. `POST /api/capture-token` stores only
 * the SHA-256 hash; the raw token is returned once and the server then genuinely cannot produce it
 * again. Apex was honest about this in a sentence. This screen goes further and makes the shape of
 * the UI carry it: the token appears in a panel that says it will not be shown again, with a copy
 * button that reports success, and it is not tucked into a list where it looks like something you
 * can come back to. Losing it costs a revoke and a re-issue, which is recoverable but annoying, and
 * an agent who assumes they can re-read it later will assume that exactly once.
 *
 * WHAT IS DELIBERATELY NOT HERE: any display of an existing token's value. The list shows the hash
 * prefix, which is what `CaptureToken.as_public()` returns and is useless as a credential. A screen
 * that appeared to show live tokens would be claiming an ability the server does not have.
 */
export function ExtensionScreen() {
  const [tokens, setTokens] = useState<CaptureTokenPublic[] | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const load = useCallback(() => {
    listCaptureTokens()
      .then(setTokens)
      .catch((e) => {
        setTokens([]);
        setError(e instanceof Error ? e.message : "Couldn't load your connections.");
      });
  }, []);

  useEffect(load, [load]);

  async function handleCreate() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await createCaptureToken("Browser extension");
      setIssued(r.token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't create a connection token.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(idPrefix: string) {
    setBusy(true);
    setError(null);
    try {
      await revokeCaptureToken(idPrefix);
      setConfirmRevoke(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't revoke that connection.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued);
      setCopied(true);
    } catch {
      // Clipboard access can be refused by the browser or unavailable over plain HTTP. The token
      // is rendered selectable either way, so this is a downgrade to "select it yourself", never a
      // dead end — and saying so beats a button that silently does nothing.
      setError("Your browser blocked the copy. Select the token above and copy it manually.");
    }
  }

  return (
    <AppShell step="extension" content="prose">
      <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Connect the browser extension</h1>
      <p style={{ color: colors.textSecondary, marginTop: 6, fontSize: 14, lineHeight: 1.5, ...blockStyle("copy") }}>
        The extension reads the comparable sales already shown on the MLS page you have open and
        sends them here. It never browses on its own, and it only ever reads the page you opened.
      </p>

      {error && (
        <p role="alert" style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, fontSize: 13, color: colors.caution }}>
          {error}
        </p>
      )}

      {/* ---- Step 1: install ------------------------------------------------ */}
      <Step n={1} title="Install it in Chrome">
        <p style={body}>
          Download the build you want, unzip it, then in Chrome open{" "}
          <code style={code}>chrome://extensions</code>, turn on <strong>Developer mode</strong>, and
          choose <strong>Load unpacked</strong> — pointing at the unzipped folder.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          <a href="/download/valubrief-extension-ai.zip" style={primaryLink}>
            Download — one-click build
          </a>
          <a href="/download/valubrief-extension.zip" style={secondaryLink}>
            Download — capture only
          </a>
        </div>
        <p style={{ ...hint, marginTop: 8 }}>
          The one-click build captures comps and opens ValuBrief to run the brief. The capture-only
          build just sends the comps. Either is fine; you can switch later.
        </p>
      </Step>

      {/* ---- Step 2: the token --------------------------------------------- */}
      <Step n={2} title="Create a connection token">
        <p style={body}>
          This is what the extension signs in with. It is tied to your account, and you can revoke it
          at any time without changing your password.
        </p>

        {issued ? (
          <div style={{ marginTop: 12, padding: "14px 16px", borderRadius: 10, background: colors.verifiedBg, border: `1px solid ${colors.verifiedLine}` }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: colors.verified }}>
              Copy this now — it will not be shown again.
            </p>
            <p style={{ margin: "4px 0 10px", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
              We only store a hash of it, so we genuinely can&apos;t show it to you a second time. If
              you lose it, revoke it below and create another — nothing else breaks.
            </p>
            <code
              // `user-select: all` so one click selects the whole string — a token broken across a
              // line and copied half-way is a support conversation nobody enjoys.
              style={{ ...code, display: "block", padding: "10px 12px", userSelect: "all", wordBreak: "break-all", fontSize: 13, background: "#fff" }}
            >
              {issued}
            </code>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={handleCopy} style={primaryButton}>
                {copied ? "Copied" : "Copy the token"}
              </button>
              <button type="button" onClick={() => { setIssued(null); setCopied(false); }} style={ghostButton}>
                I&apos;ve saved it
              </button>
            </div>
          </div>
        ) : (
          <button className="vb-btn" type="button" onClick={handleCreate} disabled={busy} aria-disabled={busy} style={{ ...primaryButton, marginTop: 12, opacity: busy ? 0.6 : 1 }}>
            {busy ? "Creating…" : "Create a connection token"}
          </button>
        )}
      </Step>

      {/* ---- Step 3: paste it ---------------------------------------------- */}
      <Step n={3} title="Paste it into the extension">
        <p style={body}>
          Click the ValuBrief icon in Chrome&apos;s toolbar, open its settings, and paste the token
          into the token field. Leave the server address as{" "}
          <code style={code}>{window.location.origin}</code> unless you have been told otherwise.
        </p>
        <p style={{ ...hint, marginTop: 8 }}>
          Then open your MLS search results, select the comparable sales you want, and use the
          extension&apos;s send button. They arrive here as a project.
        </p>
      </Step>

      {/* ---- Existing connections ------------------------------------------ */}
      <section style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${colors.border}` }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Your connections</h2>
        <p style={{ ...hint, marginTop: 4, marginBottom: 12 }}>
          One per browser or machine is a reasonable habit — then losing a laptop costs you one
          revoke, not every connection you have.
        </p>

        {tokens === null ? (
          <p style={{ fontSize: 13, color: colors.textSecondary }}>Loading…</p>
        ) : tokens.length === 0 ? (
          <p style={{ fontSize: 13, color: colors.textSecondary }}>
            No connections yet. Create one above and the extension can sign in.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tokens.map((t) => (
              <div
                key={t.id_prefix}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 12px", background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 8 }}
              >
                <div style={{ fontSize: 13 }}>
                  <strong>{t.label || "Browser extension"}</strong>
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                    {/* The hash prefix, not the token. It identifies the row and is worthless as a
                        credential — which is the only reason it is safe to print. */}
                    Added {(t.created_at || "").slice(0, 10) || "—"} · ref {t.id_prefix}
                  </div>
                </div>
                {confirmRevoke === t.id_prefix ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: colors.caution }}>
                      Revoke? The extension using it stops working immediately.
                    </span>
                    <button type="button" onClick={() => handleRevoke(t.id_prefix)} disabled={busy} style={dangerButton}>
                      Revoke
                    </button>
                    <button type="button" onClick={() => setConfirmRevoke(null)} style={ghostButton}>
                      Keep
                    </button>
                  </div>
                ) : (
                  // Two steps, because this is not undoable and the row it sits on is small. A
                  // one-click revoke next to a list is how somebody disconnects the wrong machine.
                  <button type="button" onClick={() => setConfirmRevoke(t.id_prefix)} style={ghostButton}>
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <p style={{ marginTop: 28, fontSize: 12, color: colors.textSecondary, lineHeight: 1.6, ...blockStyle("copy") }}>
        Comps sent from the extension land in a project on your account. Picking a project up inside
        this app is the next piece of work — until it ships, run the brief from the current app and
        the comps are already there waiting.
      </p>
    </AppShell>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          aria-hidden="true"
          style={{ width: 22, height: 22, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, background: colors.accentSoft, color: colors.accent, border: `1px solid ${colors.accent}` }}
        >
          {n}
        </span>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h2>
      </div>
      <div style={{ marginTop: 8, paddingLeft: 32 }}>{children}</div>
    </section>
  );
}

const body: React.CSSProperties = { margin: 0, fontSize: 14, lineHeight: 1.6, color: colors.textPrimary, ...blockStyle("copy") };
const hint: React.CSSProperties = { margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, ...blockStyle("copy") };
const code: React.CSSProperties = {
  fontFamily: typeTokens.fontMono,
  background: colors.bgApp,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  padding: "1px 5px",
};
const primaryButton: React.CSSProperties = {
  padding: "9px 16px", borderRadius: 8, border: "none", background: colors.accent,
  color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const ghostButton: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
  background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer",
};
const dangerButton: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 8, border: "none", background: colors.caution,
  color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const primaryLink: React.CSSProperties = { ...primaryButton, textDecoration: "none", display: "inline-block" };
const secondaryLink: React.CSSProperties = { ...ghostButton, textDecoration: "none", display: "inline-block" };
